import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import "./CheckIn.css";
import { CuteLoader } from "@/components/CuteLoader";

export default function CheckIn() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [selectedGoal, setSelectedGoal] = useState(null);

  useEffect(() => {
    fetchCheckInData();
  }, []);

  const fetchCheckInData = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    try {
      const [dash, home] = await Promise.all([
        api.get("/employee/dashboard", { signal: controller.signal }),
        api.get("/employee/home", { signal: controller.signal }),
      ]);
      setData({ ...dash.data, _home: home.data });
    } catch (error) {
      console.error("CheckIn fetch error:", error);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (currentSlide < 5) {
      setCurrentSlide(currentSlide + 1);
    } else {
      if (selectedGoal) {
        saveGoal(selectedGoal);
      }
      navigate("/employee/home");
    }
  };

  const handleBack = () => {
    if (currentSlide > 1) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const [touchStartX, setTouchStartX] = useState(null);
  const onTouchStart = (e) => setTouchStartX(e.changedTouches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStartX === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (dx > 60) handleBack();       // swipe right → back
    else if (dx < -60) handleContinue(); // swipe left → advance
    setTouchStartX(null);
  };

  const saveGoal = async (goal) => {
    const map = {
      hydrafacial: { goal_type: "services_per_week", goal_text: "Book 4 HydraFacial Platinums" },
      custom: { goal_type: "custom", goal_text: "A personal focus" },
    };
    const payload = map[goal] || { goal_type: "custom", goal_text: String(goal) };
    try {
      await api.post("/check-ins/set-goal", payload);
    } catch (error) {
      console.error("Goal save error:", error);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#231E1A] to-[#3A2A1E] flex items-center justify-center px-6">
        <div className="text-center">
          <div className="text-[#B0905E] text-lg mb-4" style={{ fontFamily: 'Cormorant, serif' }}>
            Could not load check-in
          </div>
          <button
            onClick={() => navigate("/employee/home")}
            className="px-6 py-3 bg-[#9C6B4A] text-white rounded-lg hover:bg-[#8B5A39] transition"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }


  if (loading || !data) {
    return <CuteLoader />;
  }

  const employee = data.employee;
  const period = data.current_period;
  const ptoBalance = data.pto_balance;

  return (
    <div className="checkin-screen" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="checkin-progress-bars">
        {[1, 2, 3, 4, 5].map((n) => (
          <div key={n} className={`checkin-progress-bar ${currentSlide >= n ? "active" : ""}`}></div>
        ))}
      </div>

      <div className="checkin-slide">
        {currentSlide === 1 && (
          <>
            <div className="checkin-slide-eyebrow">YOUR CHECK-IN · 4 MIN, JUST FOR YOU</div>
            <div className="checkin-slide-headline">
              Here's where you are, <em>{employee.display_name || employee.name?.split(" ")[0]}</em>.
            </div>
            <div className="checkin-body-copy">
              You're doing great work. Let's set a focus for the next two weeks.
            </div>
          </>
        )}

        {currentSlide === 2 && (
          <>
            <div className="checkin-slide-eyebrow">YOUR WORK</div>
            <div className="checkin-slide-headline">Keep doing <em>what you do best.</em></div>
            <div className="checkin-body-copy">
              Your contribution matters, and we see it.
            </div>
          </>
        )}

        {currentSlide === 3 && (
          <>
            <div className="checkin-slide-eyebrow">YOUR IMPACT</div>
            <div className="checkin-slide-headline">You make <em>GT what it is.</em></div>
            <div className="checkin-body-copy">
              Every detail, every client interaction — it all matters.
            </div>
          </>
        )}

        {currentSlide === 4 && (
          <>
            <div className="checkin-slide-eyebrow">PICK YOUR FOCUS</div>
            <div className="checkin-slide-headline">
              What feels right for the <em>next two weeks?</em>
            </div>

            <div className="checkin-goal-cards">
              <div
                className={`checkin-goal-card ${selectedGoal === "hydrafacial" ? "selected" : ""}`}
                onClick={() => setSelectedGoal("hydrafacial")}
              >
                <div className="checkin-goal-card-title">Book 4 HydraFacial Platinums</div>
                <div className="checkin-goal-card-sub">Your signature service</div>
              </div>
              <div
                className={`checkin-goal-card ${selectedGoal === "custom" ? "selected" : ""}`}
                onClick={() => setSelectedGoal("custom")}
              >
                <div className="checkin-goal-card-title">Write my own</div>
                <div className="checkin-goal-card-sub">Something personal</div>
              </div>
            </div>

            <div className="checkin-body-copy">
              Whatever you choose lives on your Home until next check-in. No pressure — just a direction.
            </div>
          </>
        )}

        {currentSlide === 5 && (
          <>
            <div className="checkin-slide-eyebrow">AND ONE MORE THING</div>
            <div className="checkin-slide-headline">
              You've earned <em>your place here.</em>
            </div>

            <div className="checkin-body-copy">
              {employee.milestones && employee.milestones.length > 0 && (
                <>You were promoted to Senior Artist in July. </>
              )}
              {ptoBalance !== null && ptoBalance !== undefined && (
                <>Time off: {ptoBalance} hrs available{data.pto_as_of ? ` · as of ${new Date(data.pto_as_of + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })} (ADP)` : ""}.</>
              )}
              {(!ptoBalance || (ptoBalance === null || ptoBalance === undefined)) && (
                <>We're here to support your growth and well-being.</>
              )}
              {data.pto_used && (
                <span data-testid="checkin-pto-used"> Time off used: {data.pto_used.lines.map((l) => `${l.check_date} · ${l.hours} hrs${l.amount != null ? ` — that's $${l.amount.toFixed(2)} paid for your vacation days.` : "."}`).join(" ")} {data.pto_used.year_total} hrs this year{data.pto_used.year_amount != null ? ` — $${data.pto_used.year_amount.toFixed(2)} paid while you were off.` : "."} (ADP)</span>
              )}
            </div>

            <div className="checkin-closing-line">See you Friday, love.</div>
          </>
        )}
      </div>

      <div className="checkin-action-row">
        {currentSlide > 1 && (
          <button className="checkin-back-button" onClick={handleBack} data-testid="checkin-back-button">
            Back
          </button>
        )}
        <button className="checkin-continue-button" onClick={handleContinue} data-testid="checkin-continue-button">
          {currentSlide < 5 ? "Continue" : "Done — back to Home"}
        </button>
      </div>
    </div>
  );
}
